export type Locale = 'en' | 'pl'

export const LOCALE_STORAGE_KEY = 'live2.locale'
export const DEFAULT_LOCALE: Locale = 'en'

type Dict = Record<string, string>

const shared = {
  en: {
    'lang.en': 'EN',
    'lang.pl': 'PL',
    'lang.switch': 'Language',

    'common.particles': 'Particles',
    'common.bonds': 'Bonds',
    'common.clusters': 'Clusters',
    'common.size': 'Size',
    'common.complexity': 'Complexity',
    'common.density': 'Density',
    'common.na': 'N/A',
    'common.retry': 'Retry',
    'common.cancel': 'Cancel',
    'common.refresh': 'Refresh',
    'common.error': 'Error',
    'common.loading': 'Loading...',
    'common.all': 'All',
    'common.save': 'Save',
    'common.load': 'Load',
    'common.close': 'Close',
    'common.avg': 'Avg',
    'common.min': 'Min',
    'common.max': 'Max',

    'landing.kicker': 'GPU • VALIDATION • OPEN SCIENCE',
    'landing.title': 'Open Prebiotic Simulator',
    'landing.tagline': 'Science, visualization, discovery.',
    'landing.subtitle':
      'Live 2.0 is a physically grounded prebiotic simulation focused on structural emergence, chemical reactions, and thermodynamic validation.',
    'landing.cta.enter': 'Enter simulation',
    'landing.cta.login': 'Sign in and start simulation',
    'landing.cta.learn': 'Learn more',
    'landing.badge.taichi': 'Taichi/CUDA',
    'landing.badge.pubchem': 'PubChem matching',
    'landing.badge.streaming': 'Real-time streaming',
    'landing.about.title': 'About Live 2.0',
    'landing.about.lead':
      'An open, physics-based window into how chemical complexity can emerge before biology — built for research, validation, and discovery.',
    'landing.about.what.title': 'What the project is',
    'landing.about.what.body':
      'Live 2.0 is a GPU-accelerated simulator of open-ended prebiotic chemistry. Instead of replaying a fixed reaction catalog, it lets molecular structures emerge from physically grounded particle interactions: bonds form and break under thermodynamic constraints, clusters grow into candidate molecules, and novel substances are detected, catalogued, and compared with real chemistry (including PubChem matching). A React interface streams the process live; the backend (Python, Taichi/CUDA, FastAPI) supports both interactive exploration and long scientific campaigns of hundreds of thousands of steps.',
    'landing.about.origin.title': 'How it was created',
    'landing.about.origin.body':
      'The project started from a research question: can meaningful molecular complexity arise in silico from physics alone, under conditions inspired by classic origin-of-life scenarios such as Miller–Urey atmospheres, hydrothermal chemistry, and formamide-rich mixtures? Live 2.0 was built as an open-science stack that couples high-performance simulation with thermodynamic checks, novelty detection, and reproducible run artifacts (configs, snapshots, datasets). Large validation campaigns — including Phase 2B multi-run studies — and a physics-based preprint on emergent molecular complexity in prebiotic simulations shaped the methodology into a tool meant for scrutiny, not only demos.',
    'landing.about.solves.title': 'Problems it solves today',
    'landing.about.solves.body':
      'It closes the gap between pretty particle demos and scientifically accountable chemistry: long-horizon runs with conservation and stability checks; discovery of emergent molecules without hardcoding every reaction; interpretation via database matching and exportable structures; and open workflows that keep configs, results, and Zenodo-backed data in one research loop. Researchers and builders can watch self-organization as it happens, then audit what actually formed.',
    'landing.about.future.title': 'Problems it can help solve next',
    'landing.about.future.body':
      'Looking ahead, Live 2.0 is positioned to systematically explore alternative planetary and prebiotic environments, detect autocatalytic cycles and reaction-network emergence, generate hypotheses for wet-lab origin-of-life experiments, and serve as shared infrastructure for collaborators studying molecular complexity. The same pipeline can grow into curated scientific datasets and APIs — turning simulation campaigns into reusable evidence for the wider community.',
    'landing.about.why.title': 'Why it matters',
    'landing.about.why.body':
      'Origin-of-life science needs tools that are both computationally scalable and scientifically accountable. Live 2.0 aims to show that molecular complexity can emerge from physical rules — and to make that process observable, measurable, and shareable. That matters for fundamental questions about how chemistry crosses into life-relevant structure, for transparent methods in computational chemistry, and for open research that others can inspect, reproduce, and extend.',
    'landing.why.title': 'Why Live 2.0?',
    'landing.why.subtitle':
      'The methodology combines performance with scientific rigor, without a rigid reaction catalog.',
    'landing.card.thermo.title': 'Thermodynamic validation',
    'landing.card.thermo.body':
      'Energy and momentum conservation, numerical stability checks, and automated quality tests.',
    'landing.card.discovery.title': 'Discovering new structures',
    'landing.card.discovery.body':
      'Cluster detection and new substance discovery with comparison against chemical databases.',
    'landing.card.gpu.title': 'GPU-first',
    'landing.card.gpu.body':
      'A pipeline optimized for long runs and interactive process preview.',
    'landing.materials.title': 'Project materials',
    'landing.materials.github': 'GitHub repository',
    'landing.materials.zenodo': 'Data on Zenodo',
    'landing.materials.contact': 'Scientific contact',
    'landing.hero.alt': 'Live 2.0 simulation preview',
    'landing.logo.alt': 'Live 2.0 logo',
    'notfound.title': 'Page not found',
    'notfound.body': 'This URL does not exist on Live 2.0.',
    'notfound.home': 'Back to homepage',

    'legend.title': 'Live 2.0 Molecular Simulation',
    'legend.viz.title': 'Visualization elements',
    'legend.viz.particles': 'Molecular particles',
    'legend.viz.energy': 'Energy field (pulses)',
    'legend.viz.bonds': 'Chemical bonds',
    'legend.viz.highEnergy': 'High energy',
    'legend.status.title': 'Simulation status',
    'legend.status.mode': 'Mode',
    'legend.status.step': 'Step',
    'legend.status.time': 'Time',
    'legend.status.particles': 'Particles',
    'legend.watch.title': 'What to watch',
    'legend.watch.bonds': 'Bond formation',
    'legend.watch.bonds.desc': 'particles join into structures',
    'legend.watch.pulses': 'Energy pulses',
    'legend.watch.pulses.desc': 'bright spots every 48 steps',
    'legend.watch.selfOrg': 'Self-organization',
    'legend.watch.selfOrg.desc': 'spontaneous cluster formation',
    'legend.watch.novelty': 'New substances',
    'legend.watch.novelty.desc': 'detection in NoveltyPanel',
    'legend.mode.open': 'Open Chemistry',
    'legend.mode.preset': 'Preset Prebiotic',

    'app.logo.alt': 'Live 2.0',
    'app.header.alt': 'Live 2.0 Header',
    'app.status.disconnected': 'Disconnected',
    'app.status.running': 'Running',
    'app.status.paused': 'Paused',
    'app.status.stopped': 'Stopped',
    'app.status.id': 'ID:',
    'app.nav.toggleApi.title': 'Toggle between Simulation and API v1 Jobs view',
    'app.nav.simulation': 'Simulation',
    'app.nav.apiJobs': 'API v1 Jobs',
    'app.nav.swarm.title': 'Swarm Learning Loop Dashboard',
    'app.nav.swarm': 'Swarm Dashboard',
    'app.mode.label': 'Mode:',
    'app.mode.aria': 'Simulation mode selection',
    'app.action.resume': 'Resume',
    'app.action.pause': 'Pause',
    'app.action.start': 'Start',
    'app.action.createStart': 'Create & Start',
    'app.action.stop': 'Stop',
    'app.action.reset': 'Reset',
    'app.action.logout': 'Logout',
    'app.auth.error.failed': 'Failed to authenticate',
    'app.auth.error.missingToken': 'Authentication failed: missing token',

    'controls.title': 'Simulation Controls',
    'controls.status': 'Status',
    'controls.wallClock': 'Wall-Clock Runtime',
    'controls.simTime': 'Simulation Time',
    'controls.stepCount': 'Step Count',
    'controls.species.label': 'Select Species (Preset Mode)',
    'controls.species.aria': 'Species selection for preset mode',
    'controls.metrics': 'Metrics',
    'controls.metric.noveltyRate': 'Novelty Rate',
    'controls.metric.healthScore': 'Health Score',
    'controls.metric.totalEnergy': 'Total Energy',
    'controls.discoveries': 'Recent Discoveries',
    'controls.discovery.props': 'Size: {size} | Complexity: {complexity}',
    'controls.snapshots': 'Snapshots',
    'controls.snapshot.filename': 'Snapshot filename:',
    'controls.snapshot.placeholder': 'Snapshot filename',
    'controls.settings.hide': 'Hide Settings',
    'controls.settings.show': 'Show Settings',
    'controls.settings.advanced': 'Advanced Settings',
    'controls.settings.autoSave': 'Auto-save snapshots',
    'controls.settings.showIds': 'Show particle IDs',
    'controls.settings.highContrast': 'High contrast mode',
    'controls.alert.saveOk': 'Snapshot saved successfully!',
    'controls.alert.saveFail': 'Failed to save snapshot',
    'controls.alert.loadOk': 'Snapshot loaded successfully!',
    'controls.alert.loadFail': 'Failed to load snapshot',

    'auth.title.login': 'Welcome Back',
    'auth.title.register': 'Create Account',
    'auth.subtitle.login': 'Sign in to access your simulation jobs',
    'auth.subtitle.register': 'Start your journey with Live 2.0',
    'auth.validation.emailRequired': 'Email is required',
    'auth.validation.emailInvalid': 'Please enter a valid email address',
    'auth.validation.passwordRequired': 'Password is required',
    'auth.validation.passwordShort': 'Password must be at least 6 characters long',
    'auth.email': 'Email Address',
    'auth.password': 'Password',
    'auth.email.placeholder': 'you@example.com',
    'auth.password.placeholder': 'Enter your password',
    'auth.password.hide': 'Hide password',
    'auth.password.show': 'Show password',
    'auth.tier': 'Subscription Tier',
    'auth.tier.hobby': 'Hobby - Free tier for exploration',
    'auth.tier.research': 'Research - For academic use',
    'auth.tier.pro': 'Pro - Professional features',
    'auth.loading.login': 'Signing in...',
    'auth.loading.register': 'Creating account...',
    'auth.submit.login': 'Sign In',
    'auth.submit.register': 'Create Account',
    'auth.switch.signUp': 'Sign Up',
    'auth.switch.noAccount': "Don't have an account?",
    'auth.switch.hasAccount': 'Already have an account?',

    'perf.title': 'Performance Monitor',
    'perf.loading': 'Loading performance data...',
    'perf.score': 'Score: {score}%',
    'perf.status.excellent': 'Excellent',
    'perf.status.good': 'Good',
    'perf.status.acceptable': 'Acceptable',
    'perf.status.poor': 'Poor',
    'perf.status.warmingUp': 'Warming up',
    'perf.fps': 'FPS',
    'perf.stepTimes': 'Step Times',
    'perf.visualization': 'Visualization',
    'perf.broadcast': 'Broadcast',
    'perf.warnings': 'Performance Warnings',
    'perf.tips.title': 'Performance Tips',
    'perf.tips.reduceParticles': 'Reduce particle count',
    'perf.tips.lowerViz': 'Lower visualization frequency',
    'perf.tips.checkResources': 'Check system resources',
    'perf.tips.restart': 'Restart simulation if needed',

    'novelty.save.title': 'Save cluster for PubChem matching',
    'novelty.field.size': 'Size:',
    'novelty.field.complexity': 'Complexity:',
    'novelty.field.bonds': 'Bonds:',
    'novelty.field.density': 'Density:',
    'novelty.field.avgMass': 'Avg Mass:',
    'novelty.atoms': '{n} atoms',
    'novelty.level.high': '🔥 High Emergence',
    'novelty.level.medium': '⚡ Medium Activity',
    'novelty.level.low': '🌱 Low Activity',
    'novelty.level.dormant': '💤 Dormant',
    'novelty.title': '🔬 PubChem Matcher',
    'novelty.emergenceRate': 'Emergence Rate:',
    'novelty.stat.novel': 'Novel Substances',
    'novelty.stat.discovered': 'Total Discovered',
    'novelty.discoveries': 'Recent Discoveries',
    'novelty.matchAll.title': 'Match all {n} clusters with PubChem database',
    'novelty.matchAll.loading': 'Matching...',
    'novelty.matchAll.label': 'Match All ({n})',
    'novelty.download.title': 'Download novel substances as JSON',
    'novelty.empty.title': '⏳ Waiting for clusters...',
    'novelty.empty.none': 'No novel substances detected yet.',
    'novelty.empty.hint': 'Clusters will appear after 100+ simulation steps.',
    'novelty.empty.bonds': '💡 Clusters need time to form through particle bonds.',
    'novelty.empty.cta': 'Click Download 🔽 button to match with PubChem! 🧪',
    'novelty.about.title': 'About Novelty:',
    'novelty.about.body':
      'Novel substances are unique molecular structures discovered during simulation. Higher emergence rates indicate active chemical evolution and new structure formation.',
    'novelty.alert.matchOk': 'Matched to {name} (CID {cid})',
    'novelty.alert.noMatch': 'No PubChem match found',
    'novelty.alert.matchFail': 'Failed to match cluster: {error}',
    'novelty.alert.matchAll': 'Matched {ok}/{total} clusters with PubChem',
    'novelty.alert.matchAllFail': 'Failed to match clusters: {error}',

    'heatmap.toggle.particles': 'Particles',
    'heatmap.toggle.energy': 'Energy Field',
    'heatmap.toggle.bonds': 'Bonds',
    'heatmap.particleSize': 'Particle Size:',
    'heatmap.energyOpacity': 'Energy Opacity:',
    'heatmap.particleSize.aria': 'Particle size slider',
    'heatmap.energyOpacity.aria': 'Energy opacity slider',
    'heatmap.connected': 'Connected',
    'heatmap.disconnected': 'Disconnected',
    'heatmap.tooltip.mass': 'Mass',
    'heatmap.tooltip.charge': 'Charge',
    'heatmap.tooltip.pos': 'Pos',

    'graph.empty': 'No clusters to display',
    'graph.title.none': 'No Connected Clusters',
    'graph.title.cluster': 'Cluster {n}',
    'graph.title.largest': 'Largest Connected Cluster',
    'graph.stat.size': 'Size: {n} particles',
    'graph.stat.bonds': 'Bonds: {n}',
    'graph.stat.density': 'Density: {n}',
    'graph.stat.avgMass': 'Avg Mass: {n}',
    'graph.stat.totalEnergy': 'Total Energy: {n}',
    'graph.stat.avgEnergy': 'Avg Energy: {n}',
    'graph.export.pngTitle': 'Save as PNG',
    'graph.export.jpgTitle': 'Save as JPG',
    'graph.export.png': 'PNG',
    'graph.export.jpg': 'JPG',
    'graph.export.info': 'Live2.0 Simulation - {n} steps',
    'graph.select.title': 'Select Cluster',
    'graph.select.item': 'Cluster {n}: {count} particles',
    'graph.help.hover': 'Hover over particles to see details. Click a cluster below to focus.',
    'graph.help.click': 'Use the buttons to export the current graph preview.',

    'api.initializing': 'Initializing API client...',
    'api.title': 'API v1 Jobs',
    'api.subtitle': 'Submit and monitor simulation jobs with results stored in Supabase Storage.',
    'api.connection.note': 'Connected to {url}',
    'api.account.title': 'Account Information',
    'api.account.email': 'Email:',
    'api.account.tier': 'Tier:',
    'api.account.status': 'Status:',
    'api.account.periodEnds': 'Period ends:',
    'api.account.apiKey': 'API Key',
    'api.usage.title': 'Usage Statistics',
    'api.usage.reactions': 'Reactions',
    'api.usage.apiCalls': 'API Calls',
    'api.billing.upgrade': 'Upgrade',
    'api.billing.manage': 'Manage Billing',
    'api.gate.title': 'Get Started',
    'api.gate.subtitle': 'Sign in or connect with an API key to manage jobs.',
    'api.gate.register': 'Create Account',
    'api.gate.login': 'Sign In',
    'api.key.or': 'Or use API key',
    'api.key.label': 'Enter your API key',
    'api.key.connect': 'Connect',
    'api.refresh': 'Refresh Jobs',
    'api.empty.loading': 'Loading jobs...',
    'api.empty.unavailable': 'API Server Unavailable',
    'api.empty.unavailableHint': 'Please start the backend server and try again.',
    'api.empty.none': 'No Jobs Found',
    'api.empty.noneHint': 'Submit a job via API to see it listed here.',
    'api.job.title': 'Job {id}',
    'api.job.download': 'Download Result',
    'api.job.progress': 'Progress',
    'api.job.error': 'Error',
    'api.job.resultUrl': 'Result URL',
    'api.job.storage': 'Stored in Supabase Storage',
    'api.job.created': 'Created:',
    'api.job.completed': 'Completed:',
    'api.error.download': 'Failed to download: {error}',
    'api.error.generic': 'Something went wrong. Please try again.',
    'api.error.connection': 'Could not connect to the API server.',
    'api.error.checkoutCancelled': 'Checkout was cancelled.',
    'api.error.notFound': 'Resource not found (404).',
    'api.error.server': 'API server error (502).',
    'api.error.billingLoad': 'Failed to load billing data',
    'api.error.checkoutSession': 'Failed to create checkout session',
    'api.error.portalSession': 'Failed to create portal session',
    'api.error.registerFailed': 'Registration failed',
    'api.error.loginFailed': 'Login failed',
    'api.key.placeholder': 'sk_live_...',

    'swarm.loading': 'Loading dashboard...',
    'swarm.error': 'Error',
    'swarm.retry': 'Retry',
    'swarm.noData': 'No data available',
    'swarm.title': 'Swarm Learning Loop',
    'swarm.subtitle': 'Multi-agent system performance and metrics',
    'swarm.range.7': 'Last 7 days',
    'swarm.range.30': 'Last 30 days',
    'swarm.range.90': 'Last 90 days',
    'swarm.newRun': 'New Run',
    'swarm.refresh': 'Refresh',
    'swarm.stat.totalRuns': 'Total Runs',
    'swarm.stat.totalRunsDesc': 'Across all agents',
    'swarm.stat.successRate': 'Success Rate',
    'swarm.stat.successRateDesc': 'Passed runs',
    'swarm.stat.avgScore': 'Average Score',
    'swarm.stat.avgScoreDesc': 'Out of 100',
    'swarm.stat.totalTasks': 'Total Tasks',
    'swarm.stat.totalTasksDesc': 'Executed tasks',
    'swarm.agents.title': 'Agent Performance',
    'swarm.agents.desc': 'Success rates and scores by agent role',
    'swarm.agents.empty': 'No agent data available',
    'swarm.table.agent': 'Agent',
    'swarm.table.tasks': 'Tasks',
    'swarm.table.success': 'Success',
    'swarm.table.score': 'Score',
    'swarm.table.passed': 'Passed',
    'swarm.table.warned': 'Warned',
    'swarm.table.failed': 'Failed',
    'swarm.breakdown.title': 'Status Breakdown',
    'swarm.breakdown.desc': 'Run status distribution',
    'swarm.breakdown.passed': 'Passed',
    'swarm.breakdown.warned': 'Warned',
    'swarm.breakdown.failed': 'Failed',
    'swarm.runs.title': 'Recent Runs',
    'swarm.runs.desc': 'Latest swarm learning loop executions',
    'swarm.runs.empty': 'No runs yet. Create a new run to get started.',
    'swarm.runs.score': 'Score:',
    'swarm.runs.tasks': 'Tasks:',
    'swarm.badge.pass': '✓ PASS',
    'swarm.badge.warn': '⚠ WARN',
    'swarm.badge.fail': '✗ FAIL',
    'swarm.search.title': 'Search Runs',
    'swarm.search.placeholder': 'Search by goal or run ID...',
    'swarm.search.searching': 'Searching...',
    'swarm.search.button': 'Search',
    'swarm.search.results': 'Search Results:',
    'swarm.create.title': 'Create New Run',
    'swarm.create.goal': 'Goal *',
    'swarm.create.goalPlaceholder': 'Describe the swarm learning goal...',
    'swarm.create.tasks': 'Tasks *',
    'swarm.create.addTask': 'Add Task',
    'swarm.create.taskLabel': 'Task {n}',
    'swarm.create.taskPlaceholder': 'Task description...',
    'swarm.create.agent': 'Agent',
    'swarm.create.add': '+ Add',
    'swarm.create.creating': 'Creating...',
    'swarm.create.submit': 'Create Run',
    'swarm.validation.goal': 'Goal is required',
    'swarm.validation.tasks': 'At least one task is required',
    'swarm.validation.uniqueIds': 'Task IDs must be unique',
    'swarm.validation.acceptance': 'Task {id} must have at least one acceptance criterion',
    'swarm.validation.dependency': 'Task {id} depends on non-existent task {dep}',
    'swarm.alert.created': 'Run created: {id}',
    'swarm.alert.status': 'Status: {status}',
    'swarm.error.notFound': 'Swarm API endpoint not found. Is the backend running?',
    'swarm.error.load': 'Failed to load dashboard',
    'swarm.error.search': 'Search failed',
    'swarm.error.create': 'Failed to create run',
    'swarm.search.desc': 'Semantic search across all runs',
    'swarm.create.taskId': 'Task ID *',
    'swarm.create.taskIdPlaceholder': 'task_001',
    'swarm.create.taskIdHint': 'Format: task_XXX',
    'swarm.create.name': 'Name *',
    'swarm.create.namePlaceholder': 'Task name',
    'swarm.create.description': 'Description',
    'swarm.create.dependencies': 'Dependencies',
    'swarm.create.selectDependency': 'Select dependency...',
    'swarm.create.inputs': 'Inputs',
    'swarm.create.inputPlaceholder': 'Input artifact name',
    'swarm.create.outputs': 'Outputs',
    'swarm.create.outputPlaceholder': 'Output artifact name',
    'swarm.create.acceptance': 'Acceptance Criteria *',
    'swarm.create.acceptancePlaceholder': 'Acceptance criterion',
  } satisfies Dict,

  pl: {
    'lang.en': 'EN',
    'lang.pl': 'PL',
    'lang.switch': 'Język',

    'common.particles': 'Cząstki',
    'common.bonds': 'Wiązania',
    'common.clusters': 'Klastry',
    'common.size': 'Rozmiar',
    'common.complexity': 'Złożoność',
    'common.density': 'Gęstość',
    'common.na': 'Brak',
    'common.retry': 'Ponów',
    'common.cancel': 'Anuluj',
    'common.refresh': 'Odśwież',
    'common.error': 'Błąd',
    'common.loading': 'Ładowanie...',
    'common.all': 'Wszystkie',
    'common.save': 'Zapisz',
    'common.load': 'Wczytaj',
    'common.close': 'Zamknij',
    'common.avg': 'Śr.',
    'common.min': 'Min',
    'common.max': 'Maks',

    'landing.kicker': 'GPU • WALIDACJA • OPEN SCIENCE',
    'landing.title': 'Open Prebiotic Simulator',
    'landing.tagline': 'Nauka, wizualizacja, odkrycia.',
    'landing.subtitle':
      'Live 2.0 to fizycznie ugruntowana symulacja prebiotyczna z naciskiem na emergencję struktur, reakcje chemiczne i walidację termodynamiczną.',
    'landing.cta.enter': 'Przejdź do symulacji',
    'landing.cta.login': 'Zaloguj i uruchom symulację',
    'landing.cta.learn': 'Dowiedz się więcej',
    'landing.badge.taichi': 'Taichi/CUDA',
    'landing.badge.pubchem': 'Matchowanie PubChem',
    'landing.badge.streaming': 'Streaming w czasie rzeczywistym',
    'landing.about.title': 'O projekcie Live 2.0',
    'landing.about.lead':
      'Otwarte, fizycznie ugruntowane okno na to, jak złożoność chemiczna może emergować przed biologią — zaprojektowane do badań, walidacji i odkryć.',
    'landing.about.what.title': 'Czym jest projekt',
    'landing.about.what.body':
      'Live 2.0 to przyspieszany GPU symulator otwartej chemii prebiotycznej. Zamiast odtwarzać sztywny katalog reakcji, pozwala strukturom molekularnym emergować z fizycznie ugruntowanych interakcji cząstek: wiązania powstają i pękają w ramach ograniczeń termodynamicznych, klastry rosną do cząsteczek-kandydatów, a nowe substancje są wykrywane, katalogowane i porównywane z realną chemią (w tym matchowaniem PubChem). Interfejs React streamuje proces na żywo; backend (Python, Taichi/CUDA, FastAPI) obsługuje zarówno eksplorację interaktywną, jak i długie kampanie naukowe o setkach tysięcy kroków.',
    'landing.about.origin.title': 'Jak powstał',
    'landing.about.origin.body':
      'Projekt wyszedł od pytania badawczego: czy sensowna złożoność molekularna może powstać in silico z samej fizyki, w warunkach inspirowanych klasycznymi scenariuszami origin-of-life — atmosferami Millera–Ureya, chemią hydrotermalną i mieszaninami formamidowymi? Live 2.0 zbudowano jako stack open science łączący wydajną symulację z kontrolami termodynamicznymi, detekcją nowości i powtarzalnymi artefaktami przebiegów (konfiguracje, snapshoty, datasety). Duże kampanie walidacyjne — w tym wielokrotne runy Phase 2B — oraz preprint o emergentnej złożoności molekularnej w symulacjach prebiotycznych ukształtowały metodologię jako narzędzie do weryfikacji, a nie tylko do dema.',
    'landing.about.solves.title': 'Jakie problemy rozwiązuje dziś',
    'landing.about.solves.body':
      'Zamyka lukę między efektownymi demami cząstek a chemią, którą da się rozliczyć naukowo: długie przebiegi z kontrolą zachowania wielkości i stabilności; odkrywanie emergentnych cząsteczek bez hardkodowania każdej reakcji; interpretacja przez matchowanie do baz i eksport struktur; oraz otwarte workflow łączące konfiguracje, wyniki i dane na Zenodo. Badacze i twórcy mogą obserwować samoorganizację na żywo, a potem zweryfikować, co faktycznie powstało.',
    'landing.about.future.title': 'Jakie problemy może pomóc rozwiązać w przyszłości',
    'landing.about.future.body':
      'W kolejnym horyzoncie Live 2.0 ma systematycznie badać alternatywne środowiska planetarne i prebiotyczne, wykrywać cykle autokatalityczne i emergencję sieci reakcji, generować hipotezy dla eksperymentów wet-lab nad pochodzeniem życia oraz służyć jako wspólna infrastruktura dla współpracowników badających złożoność molekularną. Ten sam pipeline może rozwinąć się w kuratorowane datasety naukowe i API — zamieniając kampanie symulacyjne w dowody wielokrotnego użytku dla szerszej społeczności.',
    'landing.about.why.title': 'Dlaczego jest istotny',
    'landing.about.why.body':
      'Badania origin-of-life potrzebują narzędzi zarazem skalowalnych obliczeniowo i rozliczalnych naukowo. Live 2.0 ma pokazać, że złożoność molekularna może emergować z praw fizyki — i uczynić ten proces obserwowalnym, mierzalnym oraz współdzielonym. To ma znaczenie dla fundamentalnych pytań o to, jak chemia przekracza próg struktur istotnych dla życia, dla transparentnych metod w chemii obliczeniowej oraz dla otwartej nauki, którą inni mogą sprawdzić, odtworzyć i rozwijać.',
    'landing.why.title': 'Dlaczego Live 2.0?',
    'landing.why.subtitle':
      'Metodologia łączy wydajność i rygor naukowy, bez sztywnego katalogu reakcji.',
    'landing.card.thermo.title': 'Walidacja termodynamiczna',
    'landing.card.thermo.body':
      'Zachowanie energii i pędu, kontrola stabilności numerycznej i automatyczne testy jakości.',
    'landing.card.discovery.title': 'Odkrywanie nowych struktur',
    'landing.card.discovery.body':
      'Wykrywanie klastrów i nowych substancji z porównaniem do baz chemicznych.',
    'landing.card.gpu.title': 'GPU-first',
    'landing.card.gpu.body':
      'Pipeline zoptymalizowany pod długie przebiegi i interaktywny podgląd procesu.',
    'landing.materials.title': 'Materiały projektu',
    'landing.materials.github': 'Repozytorium GitHub',
    'landing.materials.zenodo': 'Dane na Zenodo',
    'landing.materials.contact': 'Kontakt naukowy',
    'landing.hero.alt': 'Podgląd symulacji Live 2.0',
    'landing.logo.alt': 'Logo Live 2.0',
    'notfound.title': 'Nie znaleziono strony',
    'notfound.body': 'Ten adres URL nie istnieje w Live 2.0.',
    'notfound.home': 'Wróć na stronę główną',

    'legend.title': 'Symulacja molekularna Live 2.0',
    'legend.viz.title': 'Elementy wizualizacji',
    'legend.viz.particles': 'Cząstki molekularne',
    'legend.viz.energy': 'Pole energii (impulsy)',
    'legend.viz.bonds': 'Wiązania chemiczne',
    'legend.viz.highEnergy': 'Wysoka energia',
    'legend.status.title': 'Status symulacji',
    'legend.status.mode': 'Tryb',
    'legend.status.step': 'Krok',
    'legend.status.time': 'Czas',
    'legend.status.particles': 'Cząstki',
    'legend.watch.title': 'Co obserwować',
    'legend.watch.bonds': 'Formowanie wiązań',
    'legend.watch.bonds.desc': 'cząstki łączą się w struktury',
    'legend.watch.pulses': 'Impulsy energii',
    'legend.watch.pulses.desc': 'jasne plamy co 48 kroków',
    'legend.watch.selfOrg': 'Samoorganizacja',
    'legend.watch.selfOrg.desc': 'spontaniczne tworzenie klastrów',
    'legend.watch.novelty': 'Nowe substancje',
    'legend.watch.novelty.desc': 'wykrywanie w NoveltyPanel',
    'legend.mode.open': 'Open Chemistry',
    'legend.mode.preset': 'Preset Prebiotic',

    'app.logo.alt': 'Live 2.0',
    'app.header.alt': 'Nagłówek Live 2.0',
    'app.status.disconnected': 'Rozłączono',
    'app.status.running': 'Działa',
    'app.status.paused': 'Wstrzymano',
    'app.status.stopped': 'Zatrzymano',
    'app.status.id': 'ID:',
    'app.nav.toggleApi.title': 'Przełącz między symulacją a API v1 Jobs',
    'app.nav.simulation': 'Symulacja',
    'app.nav.apiJobs': 'API v1 Jobs',
    'app.nav.swarm.title': 'Panel Swarm Learning Loop',
    'app.nav.swarm': 'Swarm Dashboard',
    'app.mode.label': 'Tryb:',
    'app.mode.aria': 'Wybór trybu symulacji',
    'app.action.resume': 'Wznów',
    'app.action.pause': 'Pauza',
    'app.action.start': 'Start',
    'app.action.createStart': 'Utwórz i uruchom',
    'app.action.stop': 'Stop',
    'app.action.reset': 'Reset',
    'app.action.logout': 'Wyloguj',
    'app.auth.error.failed': 'Nie udało się zalogować',
    'app.auth.error.missingToken': 'Logowanie nieudane: brak tokenu',

    'controls.title': 'Sterowanie symulacją',
    'controls.status': 'Status',
    'controls.wallClock': 'Czas rzeczywisty',
    'controls.simTime': 'Czas symulacji',
    'controls.stepCount': 'Liczba kroków',
    'controls.species.label': 'Wybierz gatunek (tryb Preset)',
    'controls.species.aria': 'Wybór gatunku dla trybu preset',
    'controls.metrics': 'Metryki',
    'controls.metric.noveltyRate': 'Wskaźnik nowości',
    'controls.metric.healthScore': 'Wskaźnik zdrowia',
    'controls.metric.totalEnergy': 'Energia całkowita',
    'controls.discoveries': 'Ostatnie odkrycia',
    'controls.discovery.props': 'Rozmiar: {size} | Złożoność: {complexity}',
    'controls.snapshots': 'Migawki',
    'controls.snapshot.filename': 'Nazwa pliku migawki:',
    'controls.snapshot.placeholder': 'Nazwa pliku migawki',
    'controls.settings.hide': 'Ukryj ustawienia',
    'controls.settings.show': 'Pokaż ustawienia',
    'controls.settings.advanced': 'Ustawienia zaawansowane',
    'controls.settings.autoSave': 'Autosave migawek',
    'controls.settings.showIds': 'Pokaż ID cząstek',
    'controls.settings.highContrast': 'Tryb wysokiego kontrastu',
    'controls.alert.saveOk': 'Migawka zapisana pomyślnie!',
    'controls.alert.saveFail': 'Nie udało się zapisać migawki',
    'controls.alert.loadOk': 'Migawka wczytana pomyślnie!',
    'controls.alert.loadFail': 'Nie udało się wczytać migawki',

    'auth.title.login': 'Witaj ponownie',
    'auth.title.register': 'Utwórz konto',
    'auth.subtitle.login': 'Zaloguj się, aby uzyskać dostęp do zadań symulacji',
    'auth.subtitle.register': 'Rozpocznij pracę z Live 2.0',
    'auth.validation.emailRequired': 'Email jest wymagany',
    'auth.validation.emailInvalid': 'Podaj prawidłowy adres email',
    'auth.validation.passwordRequired': 'Hasło jest wymagane',
    'auth.validation.passwordShort': 'Hasło musi mieć co najmniej 6 znaków',
    'auth.email': 'Adres email',
    'auth.password': 'Hasło',
    'auth.email.placeholder': 'ty@example.com',
    'auth.password.placeholder': 'Wprowadź hasło',
    'auth.password.hide': 'Ukryj hasło',
    'auth.password.show': 'Pokaż hasło',
    'auth.tier': 'Plan subskrypcji',
    'auth.tier.hobby': 'Hobby — darmowy plan do eksploracji',
    'auth.tier.research': 'Research — do użytku akademickiego',
    'auth.tier.pro': 'Pro — funkcje profesjonalne',
    'auth.loading.login': 'Logowanie...',
    'auth.loading.register': 'Tworzenie konta...',
    'auth.submit.login': 'Zaloguj się',
    'auth.submit.register': 'Utwórz konto',
    'auth.switch.signUp': 'Zarejestruj się',
    'auth.switch.noAccount': 'Nie masz konta?',
    'auth.switch.hasAccount': 'Masz już konto?',

    'perf.title': 'Monitor wydajności',
    'perf.loading': 'Ładowanie danych wydajności...',
    'perf.score': 'Wynik: {score}%',
    'perf.status.excellent': 'Doskonała',
    'perf.status.good': 'Dobra',
    'perf.status.acceptable': 'Akceptowalna',
    'perf.status.poor': 'Słaba',
    'perf.status.warmingUp': 'Rozgrzewka',
    'perf.fps': 'FPS',
    'perf.stepTimes': 'Czasy kroków',
    'perf.visualization': 'Wizualizacja',
    'perf.broadcast': 'Broadcast',
    'perf.warnings': 'Ostrzeżenia wydajności',
    'perf.tips.title': 'Wskazówki wydajności',
    'perf.tips.reduceParticles': 'Zmniejsz liczbę cząstek',
    'perf.tips.lowerViz': 'Obniż częstotliwość wizualizacji',
    'perf.tips.checkResources': 'Sprawdź zasoby systemu',
    'perf.tips.restart': 'W razie potrzeby zrestartuj symulację',

    'novelty.save.title': 'Zapisz klaster do matchowania PubChem',
    'novelty.field.size': 'Rozmiar:',
    'novelty.field.complexity': 'Złożoność:',
    'novelty.field.bonds': 'Wiązania:',
    'novelty.field.density': 'Gęstość:',
    'novelty.field.avgMass': 'Śr. masa:',
    'novelty.atoms': '{n} atomów',
    'novelty.level.high': '🔥 Wysoka emergencja',
    'novelty.level.medium': '⚡ Średnia aktywność',
    'novelty.level.low': '🌱 Niska aktywność',
    'novelty.level.dormant': '💤 Uśpione',
    'novelty.title': '🔬 PubChem Matcher',
    'novelty.emergenceRate': 'Wskaźnik emergencji:',
    'novelty.stat.novel': 'Nowe substancje',
    'novelty.stat.discovered': 'Łącznie odkryte',
    'novelty.discoveries': 'Ostatnie odkrycia',
    'novelty.matchAll.title': 'Dopasuj wszystkie {n} klastrów do bazy PubChem',
    'novelty.matchAll.loading': 'Dopasowywanie...',
    'novelty.matchAll.label': 'Dopasuj wszystkie ({n})',
    'novelty.download.title': 'Pobierz nowe substancje jako JSON',
    'novelty.empty.title': '⏳ Oczekiwanie na klastry...',
    'novelty.empty.none': 'Nie wykryto jeszcze nowych substancji.',
    'novelty.empty.hint': 'Klastry pojawią się po 100+ krokach symulacji.',
    'novelty.empty.bonds': '💡 Klastry potrzebują czasu, by powstać z wiązań cząstek.',
    'novelty.empty.cta': 'Kliknij Pobierz 🔽, aby dopasować do PubChem! 🧪',
    'novelty.about.title': 'O nowości:',
    'novelty.about.body':
      'Nowe substancje to unikalne struktury molekularne odkryte podczas symulacji. Wyższy wskaźnik emergencji oznacza aktywną ewolucję chemiczną i powstawanie nowych struktur.',
    'novelty.alert.matchOk': 'Dopasowano do {name} (CID {cid})',
    'novelty.alert.noMatch': 'Brak dopasowania w PubChem',
    'novelty.alert.matchFail': 'Nie udało się dopasować klastra: {error}',
    'novelty.alert.matchAll': 'Dopasowano {ok}/{total} klastrów do PubChem',
    'novelty.alert.matchAllFail': 'Nie udało się dopasować klastrów: {error}',

    'heatmap.toggle.particles': 'Cząstki',
    'heatmap.toggle.energy': 'Pole energii',
    'heatmap.toggle.bonds': 'Wiązania',
    'heatmap.particleSize': 'Rozmiar cząstek:',
    'heatmap.energyOpacity': 'Krycie energii:',
    'heatmap.particleSize.aria': 'Suwak rozmiaru cząstek',
    'heatmap.energyOpacity.aria': 'Suwak krycia energii',
    'heatmap.connected': 'Połączono',
    'heatmap.disconnected': 'Rozłączono',
    'heatmap.tooltip.mass': 'Masa',
    'heatmap.tooltip.charge': 'Ładunek',
    'heatmap.tooltip.pos': 'Poz.',

    'graph.empty': 'Brak klastrów do wyświetlenia',
    'graph.title.none': 'Brak połączonych klastrów',
    'graph.title.cluster': 'Klaster {n}',
    'graph.title.largest': 'Największy połączony klaster',
    'graph.stat.size': 'Rozmiar: {n} cząstek',
    'graph.stat.bonds': 'Wiązania: {n}',
    'graph.stat.density': 'Gęstość: {n}',
    'graph.stat.avgMass': 'Śr. masa: {n}',
    'graph.stat.totalEnergy': 'Energia całkowita: {n}',
    'graph.stat.avgEnergy': 'Śr. energia: {n}',
    'graph.export.pngTitle': 'Zapisz jako PNG',
    'graph.export.jpgTitle': 'Zapisz jako JPG',
    'graph.export.png': 'PNG',
    'graph.export.jpg': 'JPG',
    'graph.export.info': 'Symulacja Live2.0 — {n} kroków',
    'graph.select.title': 'Wybierz klaster',
    'graph.select.item': 'Klaster {n}: {count} cząstek',
    'graph.help.hover': 'Najedź na cząstki, aby zobaczyć szczegóły. Kliknij klaster poniżej, aby go wybrać.',
    'graph.help.click': 'Użyj przycisków, aby wyeksportować podgląd grafu.',

    'api.initializing': 'Inicjalizacja klienta API...',
    'api.title': 'API v1 Jobs',
    'api.subtitle': 'Przesyłaj i monitoruj zadania symulacji z wynikami w Supabase Storage.',
    'api.connection.note': 'Połączono z {url}',
    'api.account.title': 'Informacje o koncie',
    'api.account.email': 'Email:',
    'api.account.tier': 'Plan:',
    'api.account.status': 'Status:',
    'api.account.periodEnds': 'Okres kończy się:',
    'api.account.apiKey': 'Klucz API',
    'api.usage.title': 'Statystyki użycia',
    'api.usage.reactions': 'Reakcje',
    'api.usage.apiCalls': 'Wywołania API',
    'api.billing.upgrade': 'Ulepsz',
    'api.billing.manage': 'Zarządzaj billingiem',
    'api.gate.title': 'Zacznij',
    'api.gate.subtitle': 'Zaloguj się lub połącz kluczem API, aby zarządzać zadaniami.',
    'api.gate.register': 'Utwórz konto',
    'api.gate.login': 'Zaloguj się',
    'api.key.or': 'Lub użyj klucza API',
    'api.key.label': 'Wprowadź klucz API',
    'api.key.connect': 'Połącz',
    'api.refresh': 'Odśwież zadania',
    'api.empty.loading': 'Ładowanie zadań...',
    'api.empty.unavailable': 'Serwer API niedostępny',
    'api.empty.unavailableHint': 'Uruchom backend i spróbuj ponownie.',
    'api.empty.none': 'Brak zadań',
    'api.empty.noneHint': 'Prześlij zadanie przez API, aby zobaczyć je tutaj.',
    'api.job.title': 'Zadanie {id}',
    'api.job.download': 'Pobierz wynik',
    'api.job.progress': 'Postęp',
    'api.job.error': 'Błąd',
    'api.job.resultUrl': 'URL wyniku',
    'api.job.storage': 'Zapisano w Supabase Storage',
    'api.job.created': 'Utworzono:',
    'api.job.completed': 'Ukończono:',
    'api.error.download': 'Pobieranie nieudane: {error}',
    'api.error.generic': 'Coś poszło nie tak. Spróbuj ponownie.',
    'api.error.connection': 'Nie udało się połączyć z serwerem API.',
    'api.error.checkoutCancelled': 'Checkout został anulowany.',
    'api.error.notFound': 'Nie znaleziono zasobu (404).',
    'api.error.server': 'Błąd serwera API (502).',
    'api.error.billingLoad': 'Nie udało się wczytać danych billingu',
    'api.error.checkoutSession': 'Nie udało się utworzyć sesji checkout',
    'api.error.portalSession': 'Nie udało się utworzyć sesji portalu',
    'api.error.registerFailed': 'Rejestracja nieudana',
    'api.error.loginFailed': 'Logowanie nieudane',
    'api.key.placeholder': 'sk_live_...',

    'swarm.loading': 'Ładowanie panelu...',
    'swarm.error': 'Błąd',
    'swarm.retry': 'Ponów',
    'swarm.noData': 'Brak danych',
    'swarm.title': 'Swarm Learning Loop',
    'swarm.subtitle': 'Wydajność i metryki systemu multi-agentowego',
    'swarm.range.7': 'Ostatnie 7 dni',
    'swarm.range.30': 'Ostatnie 30 dni',
    'swarm.range.90': 'Ostatnie 90 dni',
    'swarm.newRun': 'Nowy przebieg',
    'swarm.refresh': 'Odśwież',
    'swarm.stat.totalRuns': 'Łącznie przebiegów',
    'swarm.stat.totalRunsDesc': 'We wszystkich agentach',
    'swarm.stat.successRate': 'Skuteczność',
    'swarm.stat.successRateDesc': 'Udane przebiegi',
    'swarm.stat.avgScore': 'Średni wynik',
    'swarm.stat.avgScoreDesc': 'Z 100',
    'swarm.stat.totalTasks': 'Łącznie zadań',
    'swarm.stat.totalTasksDesc': 'Wykonane zadania',
    'swarm.agents.title': 'Wydajność agentów',
    'swarm.agents.desc': 'Skuteczność i wyniki wg roli agenta',
    'swarm.agents.empty': 'Brak danych agentów',
    'swarm.table.agent': 'Agent',
    'swarm.table.tasks': 'Zadania',
    'swarm.table.success': 'Sukces',
    'swarm.table.score': 'Wynik',
    'swarm.table.passed': 'OK',
    'swarm.table.warned': 'Ostrzeżenia',
    'swarm.table.failed': 'Błędy',
    'swarm.breakdown.title': 'Rozkład statusów',
    'swarm.breakdown.desc': 'Rozkład statusów przebiegów',
    'swarm.breakdown.passed': 'OK',
    'swarm.breakdown.warned': 'Ostrzeżenia',
    'swarm.breakdown.failed': 'Błędy',
    'swarm.runs.title': 'Ostatnie przebiegi',
    'swarm.runs.desc': 'Najnowsze wykonania Swarm Learning Loop',
    'swarm.runs.empty': 'Brak przebiegów. Utwórz nowy, aby zacząć.',
    'swarm.runs.score': 'Wynik:',
    'swarm.runs.tasks': 'Zadania:',
    'swarm.badge.pass': '✓ PASS',
    'swarm.badge.warn': '⚠ WARN',
    'swarm.badge.fail': '✗ FAIL',
    'swarm.search.title': 'Szukaj przebiegów',
    'swarm.search.placeholder': 'Szukaj po celu lub ID...',
    'swarm.search.searching': 'Szukanie...',
    'swarm.search.button': 'Szukaj',
    'swarm.search.results': 'Wyniki wyszukiwania:',
    'swarm.create.title': 'Utwórz nowy przebieg',
    'swarm.create.goal': 'Cel *',
    'swarm.create.goalPlaceholder': 'Opisz cel uczenia swarm...',
    'swarm.create.tasks': 'Zadania *',
    'swarm.create.addTask': 'Dodaj zadanie',
    'swarm.create.taskLabel': 'Zadanie {n}',
    'swarm.create.taskPlaceholder': 'Opis zadania...',
    'swarm.create.agent': 'Agent',
    'swarm.create.add': '+ Dodaj',
    'swarm.create.creating': 'Tworzenie...',
    'swarm.create.submit': 'Utwórz przebieg',
    'swarm.validation.goal': 'Cel jest wymagany',
    'swarm.validation.tasks': 'Wymagane jest co najmniej jedno zadanie',
    'swarm.validation.uniqueIds': 'ID zadań muszą być unikalne',
    'swarm.validation.acceptance': 'Zadanie {id} musi mieć co najmniej jedno kryterium akceptacji',
    'swarm.validation.dependency': 'Zadanie {id} zależy od nieistniejącego zadania {dep}',
    'swarm.alert.created': 'Utworzono przebieg: {id}',
    'swarm.alert.status': 'Status: {status}',
    'swarm.error.notFound': 'Nie znaleziono endpointu Swarm API. Czy backend działa?',
    'swarm.error.load': 'Nie udało się wczytać panelu',
    'swarm.error.search': 'Wyszukiwanie nieudane',
    'swarm.error.create': 'Nie udało się utworzyć przebiegu',
    'swarm.search.desc': 'Wyszukiwanie semantyczne we wszystkich przebiegach',
    'swarm.create.taskId': 'ID zadania *',
    'swarm.create.taskIdPlaceholder': 'task_001',
    'swarm.create.taskIdHint': 'Format: task_XXX',
    'swarm.create.name': 'Nazwa *',
    'swarm.create.namePlaceholder': 'Nazwa zadania',
    'swarm.create.description': 'Opis',
    'swarm.create.dependencies': 'Zależności',
    'swarm.create.selectDependency': 'Wybierz zależność...',
    'swarm.create.inputs': 'Wejścia',
    'swarm.create.inputPlaceholder': 'Nazwa artefaktu wejściowego',
    'swarm.create.outputs': 'Wyjścia',
    'swarm.create.outputPlaceholder': 'Nazwa artefaktu wyjściowego',
    'swarm.create.acceptance': 'Kryteria akceptacji *',
    'swarm.create.acceptancePlaceholder': 'Kryterium akceptacji',
  } satisfies Dict,
} as const

export const messages = {
  en: shared.en,
  pl: shared.pl,
}

export type MessageKey = keyof typeof shared.en

export function isLocale(value: string | null | undefined): value is Locale {
  return value === 'en' || value === 'pl'
}

export function localeToBcp47(locale: Locale): string {
  return locale === 'pl' ? 'pl-PL' : 'en-US'
}

export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>
): string {
  let text: string = messages[locale][key] ?? messages.en[key] ?? key
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.split(`{${name}}`).join(String(value))
    }
  }
  return text
}
